/**
 * Enhanced Load More Pagination for Shopify
 * Class-based implementation with customizer support
 */
class LoadMorePagination {
  constructor() {
    this.selectors = {
      productGrid: '#product-grid .row',
      productItems: '.col',
      loadMoreBtn: '.load-more-button',
      noMoreProducts: '.no-more-products'
    };
    
    this.state = {
      isLoading: false,
      currentPage: 1,
      totalPages: 1
    };
    
    // Bind methods to maintain 'this' context
    this.loadMoreProducts = this.loadMoreProducts.bind(this);
    this.handleSectionChange = this.handleSectionChange.bind(this);
    
    console.log('LoadMorePagination initialized');
    this.init();
    this.setupFilterEvents();
    this.setupThemeEditorListeners();
  }
  
  init() {
    // Load More Button
    const loadMoreBtn = document.querySelector(this.selectors.loadMoreBtn);
    if (loadMoreBtn) {
      console.log('Load More button found');
      this.state.currentPage = parseInt(loadMoreBtn.dataset.currentPage || 1);
      this.state.totalPages = parseInt(loadMoreBtn.dataset.totalPages || 1);
      
      // Remove existing listener to prevent duplicates
      loadMoreBtn.removeEventListener('click', this.loadMoreProducts);
      
      // Add event listener
      loadMoreBtn.addEventListener('click', this.loadMoreProducts);
    } else {
      console.log('Load More button not found');
    }
  }
  
  destroy() {
    console.log('Destroying LoadMorePagination instance');
    
    // Remove event listeners
    const loadMoreBtn = document.querySelector(this.selectors.loadMoreBtn);
    if (loadMoreBtn) {
      loadMoreBtn.removeEventListener('click', this.loadMoreProducts);
    }
  }
  
  setupFilterEvents() {
    // Add hidden inputs to filter forms
    const filterForms = document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile');
    
    filterForms.forEach(form => {
      // Check if pagination type input already exists
      let paginationTypeInput = form.querySelector('input[name="pagination_type"]');
      if (!paginationTypeInput) {
        // Add hidden input for pagination type
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'pagination_type';
        input.value = 'load_more';
        form.appendChild(input);
      } else {
        // Update existing input
        paginationTypeInput.value = 'load_more';
      }
      
      // Add products per load input if needed
      let productsPerLoadInput = form.querySelector('input[name="products_per_load"]');
      if (!productsPerLoadInput) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'products_per_load';
        input.value = this.getProductsPerLoad();
        form.appendChild(input);
      } else {
        // Update existing input
        productsPerLoadInput.value = this.getProductsPerLoad();
      }
    });
    
    // Add pagination type to facet-remove links (clear filters)
    const facetRemoveLinks = document.querySelectorAll('facet-remove a');
    facetRemoveLinks.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      const urlParams = new URLSearchParams(url.search);
      
      if (!urlParams.has('pagination_type')) {
        urlParams.set('pagination_type', 'load_more');
        urlParams.set('products_per_load', this.getProductsPerLoad());
        url.search = urlParams.toString();
        link.href = url.toString();
      }
    });
  }
  
  /**
   * Set up event listeners for Shopify theme editor events
   */
  setupThemeEditorListeners() {
    document.addEventListener('shopify:section:load', this.handleSectionChange);
    document.addEventListener('shopify:section:unload', this.handleSectionChange); 
    document.addEventListener('shopify:section:select', this.handleSectionChange);
    document.addEventListener('shopify:section:deselect', this.handleSectionChange);
    document.addEventListener('shopify:block:select', this.handleSectionChange);
    document.addEventListener('shopify:block:deselect', this.handleSectionChange);
  }
  
  /**
   * Handle section changes in theme editor
   */
  handleSectionChange(event) {
    // Check if this event affects our product grid
    const productGrid = event.target.querySelector('#product-grid');
    if (!productGrid) return;
    
    console.log('Theme editor change detected, reinitializing pagination...');
    
    // Reinitialize with short delay to ensure DOM is updated
    setTimeout(() => {
      this.destroy();
      this.init();
      this.setupFilterEvents();
    }, 300);
  }
  
  getProductsPerLoad() {
    const loadMoreBtn = document.querySelector(this.selectors.loadMoreBtn);
    if (loadMoreBtn && loadMoreBtn.dataset.productsPerLoad) {
      return Number(loadMoreBtn.dataset.productsPerLoad) || 4;
    }
    
    // Get from URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('products_per_load')) {
      return urlParams.get('products_per_load');
    }
    
    return 4; // Default fallback
  }
  
  getNextPageUrl() {
    const nextPageNumber = this.state.currentPage + 1;
    if (nextPageNumber > this.state.totalPages) return null;
    
    // Create a new URL to preserve all current URL parameters
    const url = new URL(window.location.href);
    const urlParams = new URLSearchParams(url.search);
    
    // Update page parameter
    urlParams.set('page', nextPageNumber);
    
    // Make sure we preserve pagination_type
    if (!urlParams.has('pagination_type')) {
      urlParams.set('pagination_type', 'load_more');
    }
    
    // Make sure we preserve products_per_load
    if (!urlParams.has('products_per_load')) {
      urlParams.set('products_per_load', this.getProductsPerLoad());
    }
    
    // Return the complete URL
    return `${url.pathname}?${urlParams.toString()}`;
  }
  
  loadMoreProducts(event) {
    if (event) event.preventDefault();
    
    if (this.state.isLoading || this.state.currentPage >= this.state.totalPages) {
      console.log('Already loading or at last page');
      return;
    }
    
    const nextUrl = this.getNextPageUrl();
    console.log('Load more clicked, loading url:', nextUrl);
    
    if (nextUrl) {
      this.fetchProducts(nextUrl);
    }
  }
  
  fetchProducts(url) {
    this.state.isLoading = true;
    
    // Flag to prevent reinitializations
    window.isLoadingMore = true;
    
    // Update UI to loading state
    const button = document.querySelector(this.selectors.loadMoreBtn);
    if (button) {
      button.textContent = 'Loading...';
      button.disabled = true;
    }
    
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then(html => {
        console.log('Received HTML response');
        
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = html;
        
        // Get existing product grid
        const productGrid = document.querySelector(this.selectors.productGrid);
        if (!productGrid) {
          console.error('Product grid not found');
          return;
        }
        
        // Find new products
        let newProducts = [];
        const searchResults = tempContainer.querySelector('.template-search__results');
        
        if (searchResults) {
          const productRow = searchResults.querySelector('.row[data-product-column]');
          if (productRow) {
            newProducts = Array.from(productRow.querySelectorAll('.col'));
          }
        }
        
        // Try alternative selectors if needed
        if (newProducts.length === 0) {
          const sections = tempContainer.querySelectorAll('section');
          for (const section of sections) {
            const productCols = section.querySelectorAll('.col');
            if (productCols.length > 0) {
              newProducts = Array.from(productCols);
              break;
            }
          }
        }
        
        // Filter out duplicates
        const existingProductHandles = this.getExistingProductHandles();
        const actualNewProducts = newProducts.filter(product => {
          let productHandle = this.getProductHandleFromElement(product);
          return !existingProductHandles.includes(productHandle);
        });
        
        console.log(`Found ${actualNewProducts.length} new products`);
        
        // Add new products to the grid
        if (actualNewProducts.length > 0) {
          actualNewProducts.forEach(product => {
            productGrid.appendChild(product.cloneNode(true));
          });
          
          // Update state
          this.state.currentPage++;
          
          // Initialize events on new products
          this.initProductEvents();
          
          // Check if we should enable the load more button
          const hasMoreProducts = actualNewProducts.length >= this.getProductsPerLoad() && 
                                this.state.currentPage < this.state.totalPages;
          
          if (hasMoreProducts) {
            if (button) {
              button.textContent = button.getAttribute('data-load-more-text') || 'Load More';
              button.disabled = false;
            }
          } else {
            if (button) {
              button.style.display = 'none';
              const noMoreMsg = document.querySelector(this.selectors.noMoreProducts);
              if (noMoreMsg) {
                noMoreMsg.style.display = 'block';
              }
            }
          }
        } else {
          // No more products
          if (button) {
            button.style.display = 'none';
            const noMoreMsg = document.querySelector(this.selectors.noMoreProducts);
            if (noMoreMsg) {
              noMoreMsg.style.display = 'block';
            }
          }
        }
        
        this.state.isLoading = false;
        
        // Reset loading flag
        setTimeout(() => {
          window.isLoadingMore = false;
        }, 300);
      })
      .catch(error => {
        console.error('Error loading products:', error);
        this.state.isLoading = false;
        window.isLoadingMore = false;
        
        if (button) {
          button.textContent = 'Error. Try Again';
          button.disabled = false;
        }
      });
  }
  
  getExistingProductHandles() {
    const productGrid = document.querySelector(this.selectors.productGrid);
    if (!productGrid) return [];
    
    const existingProducts = productGrid.querySelectorAll(this.selectors.productItems);
    return Array.from(existingProducts).map(product => {
      return this.getProductHandleFromElement(product);
    }).filter(Boolean);
  }
  
  getProductHandleFromElement(element) {
    // Method 1: From product URL
    const productLinks = element.querySelectorAll('a[href*="/products/"]');
    for (const link of productLinks) {
      const href = link.getAttribute('href');
      if (href) {
        const match = href.match(/\/products\/([a-zA-Z0-9-_]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }
    }
    
    // Method 2: From data attributes
    const productCard = element.querySelector('[data-product-id], [data-product-handle]');
    if (productCard) {
      return productCard.dataset.productHandle || productCard.dataset.productId;
    }
    
    // Method 3: From product title
    const productTitle = element.querySelector('.product-grid-item__title')?.textContent || '';
    if (productTitle) {
      return 'product-' + productTitle.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }
    
    // Fallback
    return 'product-' + Math.random().toString(36).substring(2, 10);
  }
  
  initProductEvents() {
    console.log('Initializing events for new products');
    
    // Re-initialize quickview if it exists
    if (typeof initQuickView === 'function') {
      initQuickView();
    }
    
    // Re-initialize color swatches if it exists
    if (typeof initColorSwatches === 'function') {
      initColorSwatches();
    }
    
    // Re-initialize any other product functionality
    // Add other product initialization functions here
    
    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('products:loaded', {
      detail: { paginationType: 'load_more' }
    }));
  }
}

// Store the instance globally for customizer access
let loadMorePagination = null;

// Initialize on DOM load
function initPagination() {
  if (document.querySelector('#product-grid') && document.querySelector('.load-more-button')) {
    if (loadMorePagination) {
      loadMorePagination.destroy();
    }
    loadMorePagination = new LoadMorePagination();
    
    // Store instance on window for customizer access
    window.loadMorePagination = loadMorePagination;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPagination);
} else {
  initPagination();
}

// Handle browser navigation
window.addEventListener('popstate', function() {
  if (loadMorePagination) {
    loadMorePagination.destroy();
    loadMorePagination = null;
  }
  
  setTimeout(() => {
    initPagination();
  }, 100);
});

// Setup filter observer
const setupFilterObserver = function() {
  if (!window.filterObserver) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      window.isFilterChanging = false;
      
      window.filterObserver = new MutationObserver(function(mutations) {
        // Skip if already processing
        if (window.isFilterChanging || window.isLoadingMore) return;
        
        // Check for filter changes
        let isFilterChange = false;
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            isFilterChange = true;
            break;
          }
        }
        
        if (isFilterChange) {
          window.isFilterChanging = true;
          
          // Clean up existing instance
          if (loadMorePagination) {
            loadMorePagination.destroy();
            loadMorePagination = null;
          }
          
          // Reinitialize after delay
          setTimeout(() => {
            initPagination();
            window.isFilterChanging = false;
          }, 500);
        }
      });
      
      window.filterObserver.observe(productGridContainer, {
        childList: true,
        subtree: false
      });
    }
  }
};

// Setup filter observer
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupFilterObserver);
} else {
  setupFilterObserver();
}